import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, Info, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FadeUp } from "../components/Shared";
import { FileUpload } from "../components/FileUpload";
import { getToken } from "../lib/authApi";

const API = process.env.REACT_APP_BACKEND_URL;

const estimate = (config, process, material, qty) => {
  if (!config || !qty) return null;
  const p = config.processes.find((x) => x.name === process);
  const m = config.materials.find((x) => x.name === material);
  if (!p || !m) return null;
  const discount = qty >= 500 ? 0.7 : qty >= 100 ? 0.8 : qty >= 50 ? 0.88 : qty >= 10 ? 0.95 : 1.0;
  const base = p.setup_charge + p.rate * qty * m.multiplier * discount;
  return [Math.floor(base * 0.85), Math.floor(base * 1.2)];
};

const fmtINR = (n) => `₹${n.toLocaleString("en-IN")}`;

const initialForm = { name: "", email: "", company: "", notes: "" };

export default function InstantQuote() {
  const [config, setConfig] = useState(null);
  const [process, setProcess] = useState("");
  const [material, setMaterial] = useState("");
  const [quantity, setQuantity] = useState("");
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/library/pricing-config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    if (!submitted) return;
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/api/quotes/${submitted.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vectron-Quote-VG-${submitted.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  const qty = parseInt(quantity, 10) || 0;
  const range = useMemo(() => estimate(config, process, material, qty), [config, process, material, qty]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!range) {
      toast.error("Select a process, material and quantity first.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/api/quotes`, {
        process, material, quantity: qty, ...form, file_ids: files.map((f) => f.id),
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setSubmitted(data);
      toast.success("Quote request submitted! Our engineering team will follow up with a firm quote.");
      setForm(initialForm);
      setFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString?.() || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="instant-quote-page">
      <section className="py-28 sm:py-36 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary mb-6 font-mono-tech">Instant Quote</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[1.05]">
              Get an indicative price <span className="font-medium text-primary">in seconds.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Pick your process, material and quantity for a live estimate — then submit your details and CAD files for a firm engineering-reviewed quote.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-7">
            <form onSubmit={submit} className="space-y-6 p-8 sm:p-10 rounded-xl bg-card border border-border" data-testid="quote-form">
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Process *</Label>
                  <Select value={process} onValueChange={setProcess}>
                    <SelectTrigger data-testid="quote-process-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {(config?.processes || []).map(({ name: p }) => (
                        <SelectItem key={p} value={p} data-testid={`quote-process-option-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Material *</Label>
                  <Select value={material} onValueChange={setMaterial}>
                    <SelectTrigger data-testid="quote-material-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {(config?.materials || []).map(({ name: m }) => (
                        <SelectItem key={m} value={m} data-testid={`quote-material-option-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="q-qty">Quantity *</Label>
                  <Input id="q-qty" type="number" min="1" max="1000000" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="e.g. 100" data-testid="quote-quantity-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 pt-4 border-t border-border">
                <div className="space-y-2">
                  <Label htmlFor="q-name">Name *</Label>
                  <Input id="q-name" value={form.name} onChange={set("name")} placeholder="Full name" data-testid="quote-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="q-email">Email *</Label>
                  <Input id="q-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="quote-email-input" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="q-company">Company</Label>
                  <Input id="q-company" value={form.company} onChange={set("company")} placeholder="Company name" data-testid="quote-company-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-notes">Notes</Label>
                <Textarea id="q-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="Tolerances, finishes, timelines..." data-testid="quote-notes-input" />
              </div>
              <FileUpload files={files} onChange={setFiles} />
              <Button type="submit" size="lg" disabled={loading} className="rounded-full px-10" data-testid="quote-submit-btn">
                {loading ? "Submitting..." : "Submit Quote Request"}
              </Button>
            </form>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-5">
            <div className="sticky top-28 p-8 sm:p-10 rounded-xl bg-slate-950 dark:bg-card border border-white/10 dark:border-border" data-testid="quote-estimate-panel">
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5 text-blue-400" strokeWidth={1.5} />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 font-mono-tech">Live Estimate</p>
              </div>
              {range ? (
                <div className="mt-8">
                  <p className="font-mono-tech text-3xl sm:text-4xl font-bold text-white" data-testid="quote-estimate-value">
                    {fmtINR(range[0])} <span className="text-slate-500 text-2xl">–</span> {fmtINR(range[1])}
                  </p>
                  <p className="mt-3 text-sm text-slate-400">{process} · {material} · {qty.toLocaleString("en-IN")} pcs</p>
                </div>
              ) : (
                <p className="mt-8 text-slate-400 text-sm" data-testid="quote-estimate-empty">
                  Select a process, material and quantity to see your indicative price range.
                </p>
              )}
              {submitted && (
                <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm text-blue-200" data-testid="quote-submitted-confirmation">
                  <p>
                    Request <span className="font-mono-tech">VG-{submitted.id.slice(0, 8).toUpperCase()}</span> received — we'll reply with a firm quote within 24 hours.
                  </p>
                  <button
                    onClick={downloadPdf}
                    disabled={pdfLoading}
                    data-testid="quote-pdf-download-btn"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/40 text-xs font-semibold text-blue-100 hover:bg-blue-500/30 transition-colors duration-200"
                  >
                    <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {pdfLoading ? "Preparing PDF..." : "Download Quote PDF"}
                  </button>
                </div>
              )}
              <div className="mt-8 pt-6 border-t border-white/10 flex gap-3 text-xs text-slate-500">
                <Info className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                <p>Indicative demo estimate only. Final pricing depends on geometry, tolerances and finishes, and is confirmed after engineering review.</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
